import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CircularIndeterminate } from "../components/Progress";

import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Button,
} from "@mui/material";
import { spacing, display } from "@mui/system";
import axios from "../../utils/axios";
import HomeIcon from "@mui/icons-material/Home";
import convertUSD from "../../utils/convertUSD";
import DepositInfo from "./components/DepositInfo";
import * as Yup from "yup";
import { Field, Formik } from "formik";
import { MultilineTextField, SingleSelect } from "../../utils/form";
import useConfirm from "../../components/useConfirm";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(display);

const Alert = styled(MuiAlert)(spacing);

function DepositRefund() {
  const { depositId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [refundOptions, setRefundOptions] = useState({ reasonRefund: [] });
  const [deposit, setDeposit] = useState({
    amount: null,
    captureId: null,
    createdAt: null,
    status: null,
    userId: null,
    id: null,
  });
  const [ConfirmDialog, confirmAction] = useConfirm(
    "Are you sure you want to update user?"
  );

  const getDeposit = useCallback(async () => {
    try {
      setLoading(true);

      const { data: refundOptions } = await axios.options(
        `/api/v1/deposits/${depositId}/refund`
      );
      setRefundOptions(refundOptions);

      const { data: deposit } = await axios.get(
        `/api/v1/deposits/${depositId}`
      );
      setDeposit(deposit);

      setLoading(false);
    } catch (error) {
      setError(error.messages);
      console.log(error);
    }
  }, [setLoading, setError, setDeposit, depositId]);

  const refundDeposit = useCallback(
    async (
      { reasonRefund, reasonText },
      { setErrors, setStatus, setSubmitting, resetForm }
    ) => {
      const isConfirmed = await confirmAction();

      if (!isConfirmed) {
        return;
      }

      try {
        setLoading(true);

        const { data: refund } = await axios.post(
          `/api/v1/deposits/${depositId}/refund`,
          {
            reasonRefund,
            reasonText,
          }
        );

        resetForm();

        navigate(`/refunds/${refund.id}`);
      } catch (error) {
        console.log(error);
        const message = error.messages || "Something went wrong";

        setStatus({ success: false });
        setErrors({ submit: message });
        setSubmitting(false);
      }
      setLoading(false);
    },
    [setLoading, setDeposit, depositId]
  );

  useEffect(() => {
    getDeposit();
  }, [getDeposit]);

  if (error) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <React.Fragment>
      <Helmet title={` Deposit ID ${depositId} Refund`} />

      <Typography variant="h3" gutterBottom display="inline">
        Refund
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/deposits">
          Deposits
        </Link>
        <Link component={NavLink} to={`/deposits/${depositId}`}>
          {depositId}
        </Link>
        <Typography>Refund</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Card>
        <CardContent>
          <Formik
            initialValues={{
              reasonRefund: refundOptions?.reasonRefund?.[0]?.value,
              reasonText: "",
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              reasonRefund: Yup.string().required("Reason is required"),
              reasonText: Yup.string().when("reasonRefund", {
                is: (reasonRefund) => reasonRefund,
                then: Yup.string().required("Reason text is required"),
                otherwise: Yup.string(),
              }),
            })}
            onSubmit={refundDeposit}
          >
            {({ errors, handleSubmit, isSubmitting }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={8}>
                  {errors.submit && (
                    <Grid item xs={12}>
                      <Alert severity="warning">{errors.submit}</Alert>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Grid
                      container
                      spacing={{ xs: 8, md: 12, lg: 20 }}
                      direction="row"
                    >
                      <Grid item xs="auto">
                        <Grid container spacing={4} direction="column">
                          <Grid item xs={12}>
                            <DepositInfo
                              loading={loading}
                              title="User ID"
                              value={deposit.userId}
                              link={`/users/${deposit.userId}`}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <DepositInfo
                              loading={loading}
                              title="Deposit Braintree ID"
                              value={deposit.transactionId}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <DepositInfo
                              loading={loading}
                              title="User Real Cash Amount for the Moment"
                              value={convertUSD(
                                deposit.userRealCashAmountForMoment
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <DepositInfo
                              loading={loading}
                              title="Refund Amount"
                              value={convertUSD(deposit.amount)}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs="auto">
                        <Grid container spacing={4} direction="column">
                          <Grid item xs={12}>
                            <Field
                              name="reasonRefund"
                              label="Select the Reason for the Refund"
                              component={SingleSelect}
                              loading={loading}
                              options={refundOptions.reasonRefund}
                              sx={{ minWidth: "280px" }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Field
                              name="reasonText"
                              label="Specify the Reason"
                              loading={loading}
                              component={MultilineTextField}
                              sx={{ minWidth: "280px" }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={4}>
                      <Grid item>
                        <Button
                          type="submit"
                          color="primary"
                          variant="contained"
                          disabled={isSubmitting || loading}
                        >
                          Confirm
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          color="primary"
                          onClick={() => navigate(`/deposits/${depositId}`)}
                          disabled={isSubmitting || loading}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </CardContent>
      </Card>
      <ConfirmDialog />
    </React.Fragment>
  );
}

export default DepositRefund;
